import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { SubscribedComponent } from '../../subscribed.component';
import { OpportunityService } from '../../shared/opportunity.service';
import { AuthService } from '../../shared/auth.service';
import { CrmSummary, Opportunity, OpportunityStatus } from '../../interfaces/opportunity';
import { Submission } from '../../interfaces/submission';
import { EditDialogComponent, EditType } from '../../dialogs/edit-dialog/edit-dialog.component';
import { Paged } from '../../interfaces/paged';
import { activeFilterKeys, DualAxisDelegate, DualAxisEvent } from '../../dual-axis/dual-axis.component';
import { ColDef, ColType, ListEntry } from '../../interfaces/colDef';
import { TreeNode } from '../../interfaces/treenode';
import { Subject } from 'rxjs';
import { TableOptions } from '../../settings/table-options/table-options.component';
import { SettingsService } from '../../shared/settings.service';
import { debounceTime } from 'rxjs/operators';
import { DualAxisComponent } from '../../dual-axis/dual-axis.component';
import { PopupDialogComponent } from '../../dialogs/popup-dialog/popup-dialog.component';
import { OpportunityDetailComponent } from '../opportunity-detail/opportunity-detail.component';
import { NewClientDialogComponent } from '../../clients/new-client-dialog/new-client-dialog.component';
import { Globals } from '../../shared/Globals';
import { ClientService } from '../../shared/client.service';
import { MarketVacancyService } from '../../shared/market-vacancy.service';
import { Client, ClientStatus } from '../../interfaces/client';
import { NotifierService } from '../../shared/notifier.service';
import { resolveOrgPick } from '../../shared/organization-pick.util';
import { DirectoryService } from '../../shared/directory.service';
import { SubmissionService } from '../../shared/submission.service';

/** Prefix marking a broker-select value as a "known market source" quick-pick
 * rather than a real Client id — resolved to one on submit. */
const SOURCE_ENTRY_PREFIX = 'src:';

interface StatusChip {
  key: OpportunityStatus;
  translateKey: string;
  emptyIcon: string;
}

@Component({
  selector: 'bst-opportunities',
  templateUrl: './opportunities.component.html',
  styleUrls: ['./opportunities.component.scss'],
  standalone: false
})
export class OpportunitiesComponent extends SubscribedComponent implements OnInit, DualAxisDelegate<Opportunity> {

  @ViewChild('cellTemplate', { static: true }) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild(DualAxisComponent) dualAxis: DualAxisComponent<Opportunity>;

  opportunities: Opportunity[] = [];
  errorMessage: string | null = null;
  activeStatus: OpportunityStatus = 'open';
  statusCounts: Record<string, number> = {};
  summary: CrmSummary | null = null;
  tabs: any[] = [];

  // DualAxis properties
  public data: TreeNode<Opportunity>[] = [];
  public stickyColumnsLeft: ColDef[] = [];
  public standardColumns: ColDef[] = [];
  public stickyColumnsRight: ColDef[] = [];
  public options: TableOptions;
  public events = new Subject<DualAxisEvent>();
  public sorts: {col: string, dir: number}[] = [];

  // Search
  public searchQuery: string = '';
  private searchSubject = new Subject<string>();

  readonly statusChips: StatusChip[] = [
    { key: 'open',     translateKey: 'crm.opp.open',     emptyIcon: 'radio-button-on-outline' },
    { key: 'won',      translateKey: 'crm.opp.won',      emptyIcon: 'award-outline' },
    { key: 'lost',     translateKey: 'crm.opp.lost',     emptyIcon: 'close-circle-outline' },
    { key: 'on_hold',  translateKey: 'crm.opp.on_hold',  emptyIcon: 'pause-circle-outline' },
    { key: 'archived', translateKey: 'crm.opp.archived', emptyIcon: 'archive-outline' },
  ];

  constructor(private opportunityService: OpportunityService,
              private clientService: ClientService,
              private marketVacancyService: MarketVacancyService,
              private dialogService: NbDialogService,
              private auth: AuthService,
              private router: Router,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private settings: SettingsService,
              private notifier: NotifierService,
              private directoryService: DirectoryService,
              private location: Location,
              private submissionService: SubmissionService) {
    super();
  }

  /** Count for a status chip. */
  chipCount(key: OpportunityStatus): number | null {
    if (!Object.keys(this.statusCounts).length) { return null; }
    return this.statusCounts[key] ?? 0;
  }

  /** Staffer/admin in a CRM-enabled tenant may create. */
  get canCreate(): boolean {
    return (this.auth.isAdmin || this.auth.isStaffer) && !!this.auth.user.getValue()?.crm_enabled;
  }

  /** The active tab's empty-state icon. */
  get activeEmptyIcon(): string {
    return this.statusChips.find(c => c.key === this.activeStatus)?.emptyIcon ?? 'briefcase-outline';
  }

  /** Open the create dialog; on success submit the picked consultant (if any)
   *  and open the new opportunity in a popup that reloads the list on close. */
  async openCreate(): Promise<void> {
    // Loaded for the BROKER field only: the end-client field is a LISTADDER
    // that searches the organisation catalogue on demand and needs no
    // preloaded list. Independent network calls — no dependency between them,
    // so run in parallel rather than paying the sum of both round trips on
    // every "add opportunity" click.
    const [clients, allClientsAnyStatus, knownSources] = await Promise.all([
      this.clientService.loadAllProspects(),
      // ARCHIVED-inclusive, for the dedup check below only - loadAllProspects()
      // (used for the visible picker list) excludes archived clients on
      // purpose, but that same exclusion let a market source name matching an
      // archived Client go unrecognised and get silently duplicated (#1217).
      this.clientService.loadAllClientsAnyStatus(),
      // Not silent on failure — APIService's errorify() always surfaces a
      // toast — but non-fatal: an empty list makes the broker field fall back
      // to the plain picker instead of blocking the dialog from opening.
      this.marketVacancyService.getSources().catch((e) => {
        console.error('vacancy sources load failed', e);
        return [] as string[];
      }),
    ]);
    const clientList: ListEntry[] = clients.map(c => ({ id: c.id, name: c.name }));

    // Known market-source quick-picks for the broker field (#1194, #1217):
    // a broker is often the platform the vacancy came from, and none of them
    // have a Client row yet. Offer the ones not already a Client by name as
    // synthetic entries — resolved to a real (found-or-created) Client id
    // below, once the dialog closes.
    const existingNames = new Set(allClientsAnyStatus.map(c => c.name.toLowerCase()));
    const sourceEntries: ListEntry[] = knownSources
      .filter(name => !existingNames.has(name.toLowerCase()))
      .map(name => ({
        id: SOURCE_ENTRY_PREFIX + name,
        name: `${name} (${this.translate.instant('crm.create.new_source_suffix')})`
      }));

    // Inline "+ New client" for both client pickers: reuses the same
    // Track-as-Prospect / Add-as-Client picker that /clients' own "+ New"
    // uses, so creating an org from here isn't forced into a full paying
    // Client — navigateOnCreate is off since this is a picker mid-form, not
    // a destination (the /clients "+ New" entry point still navigates there).
    const addClient = async (): Promise<ListEntry | null> => {
      const result = await this.dialogService
        .open<NewClientDialogComponent>(NewClientDialogComponent, {
          hasBackdrop: true,
          closeOnEsc: true,
          autoFocus: false,
          context: { navigateOnCreate: false },
        })
        .onClose.toPromise();
      return result?.clientId ? { id: result.clientId, name: result.name } : null;
    };

    // Non-fatal: a failed catalogue load (already toasted) degrades to an
    // empty picker instead of killing the dialog before it opens.
    const roles = await this.directoryService.getProjectRoles().catch((e) => {
      console.error('project roles load failed', e);
      return [];
    });

    const defs: ColDef[] = [
      {
        name: 'for_Client_id',
        target: 'organization',
        title: this.translate.instant('crm.create.end_client'),
        action: { label: this.translate.instant('crm.create.add_client'), handler: addClient },
        type: ColType.LISTADDER,
        length: 1,
        required: true
      },
      {
        name: 'title',
        title: this.translate.instant('crm.create.title'),
        type: ColType.STRING,
        length: 100,
        required: true
      },
      {
        name: 'description',
        title: this.translate.instant('crm.create.description'),
        type: ColType.AREA,
        length: 500,
        style: { width: '100%', minHeight: '8rem' },
        required: false
      },
      {
        name: 'via_Client_id',
        title: this.translate.instant('crm.create.broker'),
        hint: this.translate.instant('crm.create.broker_hint'),
        action: { label: this.translate.instant('crm.create.add_client'), handler: addClient },
        // Broker stays a SELECT over scrape sources + existing clients, and
        // deliberately NOT the organisation picker the end-client field above
        // uses. The catalogue is a directory of opdrachtgevers that excludes
        // brokers by construction: config/organizations.php lists
        // brokers.companies as "never the real opdrachtgever", and
        // excluded_platforms carry no org at all. Pointing this field there
        // would offer a list that cannot contain the answer.
        //
        // `target` is dropped with the LISTADDER: it is LISTADDER-only
        // (colDef.ts), so on a SELECT it read as if this field searched the
        // catalogue while doing nothing.
        //
        // An unlisted broker is still creatable: `action` wires the same
        // "+ New client" dialog, which SELECT supports, so a detachering party
        // you have not dealt with before becomes a prospect Client here.
        type: ColType.SELECT,
        list: [{ id: null as any, name: this.translate.instant('crm.none') }, ...sourceEntries, ...clientList],
        required: false
      },
      {
        name: 'rate_target',
        title: this.translate.instant('crm.rate_indication'),
        type: ColType.CURRENCY,
        required: false
      },
      {
        name: 'location',
        title: this.translate.instant('crm.location'),
        type: ColType.STRING,
        length: 100,
        required: false
      },
      {
        name: 'submission_deadline',
        title: this.translate.instant('crm.deadline'),
        type: ColType.DATE,
        required: false
      },
      {
        name: 'skills',
        target: 'skill',
        title: this.translate.instant('crm.create.skills'),
        type: ColType.LISTADDER,
        required: false
      },
      {
        name: 'ProjectRole_id',
        title: this.translate.instant('crm.create.role'),
        type: ColType.SELECT,
        list: [{ id: null as any, name: this.translate.instant('crm.none') },
               ...roles.map(r => ({ id: r.id, name: r.name }))],
        required: false
      },
      {
        // Same `member` search target as the detail page's "Add submission"
        // dialog; the pick becomes a Submission right after the create (#1427).
        name: 'consultant',
        target: 'member',
        title: this.translate.instant('crm.consultant'),
        type: ColType.LISTADDER,
        length: 1,
        required: false
      }
    ];

    const result = await this.dialogService
      .open<EditDialogComponent<any>>(EditDialogComponent, {
        dialogClass: 'wide-edit',
        context: {
          type: EditType.ADD,
          title: this.translate.instant('crm.col.opportunity'),
          description: defs,
          data: {}
        }
      })
      .onClose.toPromise();

    if (result) {
      try {
        // A quick-picked market source isn't a real Client id yet — resolve it
        // before the opportunity is saved. Re-fetches (any status, including
        // archived - same reason as the dedup check in openCreate()) right
        // before checking, rather than reusing the snapshot from when the
        // dialog opened, which by construction never contains this name — it's
        // exactly why the entry was offered as a quick-pick — so a Client
        // created for this name in the meantime, by another concurrent flow,
        // is still caught. Narrows the duplicate-Client window; a genuinely
        // simultaneous pick of the same never-before-seen source can still
        // create two — low-stakes (a spare prospect Client), not guarded further.
        //
        // These broker Clients carry no Organization_id, and that is now the
        // settled answer rather than a deferral: the catalogue is a directory
        // of opdrachtgevers and excludes brokers by construction, so there is
        // no org for them to bridge to. The end client, which IS an
        // opdrachtgever, goes through the catalogue below.
        let brokerId: string | number | null = result.via_Client_id || null;
        if (typeof brokerId === 'string' && brokerId.startsWith(SOURCE_ENTRY_PREFIX)) {
          const sourceName = brokerId.slice(SOURCE_ENTRY_PREFIX.length);
          const freshClients = await this.clientService.loadAllClientsAnyStatus();
          const existing = freshClients.find(c => c.name.toLowerCase() === sourceName.toLowerCase());
          brokerId = existing
            ? existing.id
            : (await this.clientService.addClient({ name: sourceName, status: ClientStatus.PROSPECT } as Client)).id;
        }

        // Only the end client is resolved through the catalogue: a picker
        // selection is either a catalogue org or a brand-new Client from its
        // own "+ New client" flow, and resolveOrgPick tells them apart.
        const endPick = resolveOrgPick(result.for_Client_id);

        const payload: Partial<Opportunity> & {for_Organization_id?: number | null, skill_ids?: number[]} = {
          for_Client_id: endPick.clientId,
          for_Organization_id: endPick.organizationId,
          via_Client_id: brokerId as number | null,
          title: result.title.trim(),
          description: result.description?.trim() || undefined,
          source: 'manual',
          rate_target: result.rate_target || null,
          location: result.location?.trim() || null,
          submission_deadline: result.submission_deadline ? Globals.toISODate(result.submission_deadline) : null,
          skill_ids: (result.skills || []).map((s: any) => s.id),
          ProjectRole_id: result.ProjectRole_id || null
        };
        const created = await this.opportunityService.create(payload as Partial<Opportunity>);
        if (created?.id) {
          if (result.consultant?.id) {
            // Own catch: post() has already toasted the failure, and the
            // opportunity IS saved, so the outer "save failed" would lie.
            // Fall through and open it; the consultant can be added there.
            try {
              await this.submissionService.create(created.id, result.consultant.id);
            } catch (e: any) {
              console.error('submission create failed', e);
            }
          }
          // Reload on the popup's close, not here: the paged list re-navigates
          // to its own URL once the fetch resolves, which would overwrite the
          // popup's share URL and leave a stray history entry.
          this.openOpportunity(created, true);
        }
      } catch (e: any) {
        console.error('opportunity create failed', e);
        // The create dialog is already closed at this point (result was
        // truthy), so without this the user sees nothing at all and has no
        // way to know the opportunity — or, on a broker-resolution failure,
        // the Client behind it — was never actually saved.
        void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initTabs();
    void this.loadSummary();

    this.stickyColumnsLeft = [
      { name: 'opportunity', title: this.translate.instant('crm.col.opportunity'), type: ColType.STRING, required: false, sort: true },
    ];

    this.standardColumns = [
      { name: 'organization', title: this.translate.instant('crm.col.organization'), type: ColType.STRING, required: false, sort: false },
      { name: 'stage', title: this.translate.instant('crm.col.stage'), type: ColType.STRING, required: false, sort: false, filter: false },
      { name: 'rate', title: this.translate.instant('crm.col.rate'), type: ColType.CURRENCY, required: false, sort: true, range: { gte: 0, lte: 500 } },
      { name: 'candidates', title: this.translate.instant('crm.col.candidates'), type: ColType.INT, required: false, sort: true, range: { gte: 0, lte: 50 } },
      { name: 'owner', title: this.translate.instant('crm.col.owner'), type: ColType.STRING, required: false },
      { name: 'deadline', title: this.translate.instant('crm.col.deadline'), type: ColType.DATE, required: false, sort: true },
      { name: 'activity', title: this.translate.instant('crm.col.last_activity'), type: ColType.DATE, required: false, sort: true },
    ];

    this.subs.push(this.route.params.subscribe(params => {
      // Old bookmarks/links may still carry the removed 'all' status (or any
      // other stale value) — redirect to 'open' rather than loading it with a
      // route that matches no tab (nb-route-tabset would show none active).
      const requested = params['status'];
      if (!this.statusChips.some(c => c.key === requested)) {
        void this.router.navigate(['/opportunities/view/open'], { replaceUrl: true });
        return;
      }
      this.activeStatus = requested;

      this.options = {
        display: this.settings.mergeTableColumns('table_crm_opportunities', this.standardColumns),
        sorts: [{ col: 'activity', dir: 2 }],
        filter: {},
        others: { status: this.activeStatus }
      };

      this.events.next(DualAxisEvent.RESET);
    }));

    this.subs.push(
      this.searchSubject.pipe(debounceTime(500)).subscribe(query => {
        this.applySearch(query);
      })
    );
  }

  // DualAxisDelegate implementation
  public optionsChange(value: TableOptions): boolean {
    return value.others.status !== this.options.others.status;
  }

  public async paged(page: number, pageSize: number): Promise<Paged<Opportunity>> {
    const filters: any = { ...(this.options.filter || {}), status: this.options.others.status };

    if (page <= 1) {
      this.data = [];
    }

    const response = await this.opportunityService.list(page, filters, this.options.sorts || [], pageSize);
    if (response?.data) {
      this.data = response.data.map(opp => ({
        id: opp.id,
        data: opp,
        children: []
      }));
      this.opportunities = this.data.map(n => n.data);
    }
    return response;
  }

  public onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  private applySearch(query: string): void {
    if (!this.options?.filter) return;
    if (query && query.trim()) {
      this.options.filter['stuff'] = query.trim();
    } else {
      delete this.options.filter['stuff'];
    }
    this.events.next(DualAxisEvent.RESET);
  }

  public clearSearch(): void {
    this.searchQuery = '';
    this.applySearch('');
  }

  public openOptions(): void {
    this.events.next(DualAxisEvent.OPENFILTERS);
  }

  public getActiveFilterCount(): number {
    return activeFilterKeys(this.options?.filter).length;
  }

  rowClass = (opp: Opportunity): string | null => {
    return null;
  };

  private initTabs(): void {
    this.tabs = this.statusChips.map(chip => ({
      title: this.translate.instant(chip.translateKey),
      route: `/opportunities/view/${chip.key}`,
      responsive: true
    }));

    // Re-resolve once translations are available
    this.subs.push(this.translate.get(this.statusChips.map(c => c.translateKey)).subscribe(t => {
      this.tabs.forEach((tab, i) => {
        tab.title = t[this.statusChips[i].translateKey];
        this.updateTabLabels();
      });
    }));
  }

  // ── Table cell helpers ───────────────────────────────────────────────────

  /**
   * Format rate_target for the Rate column.
   * rate_target set → €85/h; else --.
   */
  formatValueRate(opp: Opportunity): string {
    if (opp.rate_target != null) {
      return '€' + opp.rate_target + '/h';
    }
    return '--';
  }

  /** Full owner name for the avatar + tooltip. Returns empty string if no owner. */
  ownerName(opp: Opportunity): string {
    if (!opp.owner) { return ''; }
    return `${opp.owner.name_first ?? ''} ${opp.owner.name_last ?? ''}`.trim();
  }

  /** Consultant name for a candidate avatar + tooltip. */
  consultantName(sub: Submission): string {
    if (!sub.consultant) { return ''; }
    return `${sub.consultant.name_first ?? ''} ${sub.consultant.name_last ?? ''}`.trim();
  }

  /** Names of the candidates hidden behind the "+N" overflow marker. */
  overflowConsultantNames(opp: Opportunity): string {
    return (opp.submissions ?? []).slice(5).map(s => this.consultantName(s)).join(', ');
  }

  /** Format deadline: "30 Jun" or null if not set. */
  formatDeadline(opp: Opportunity): string {
    if (!opp.submission_deadline) { return '--'; }
    return moment(opp.submission_deadline).format('D MMM');
  }

  /**
   * Relative suffix for deadline: "· in 9d" (future), "· 2d ago" (past).
   * Only shown when status is open. Text is translated via i18n keys.
   */
  deadlineRelative(opp: Opportunity): string | null {
    if (!opp.submission_deadline || opp.status !== 'open') { return null; }
    const diff = moment(opp.submission_deadline).diff(moment(), 'days');
    if (diff === 0) {
      return '· ' + this.translate.instant('crm.time.today');
    }
    if (diff > 0) {
      return '· ' + this.translate.instant('crm.time.in_days', { n: diff });
    }
    return '· ' + this.translate.instant('crm.time.days_ago', { n: Math.abs(diff) });
  }

  /** True if deadline is past and status is open (danger color). */
  deadlinePastDue(opp: Opportunity): boolean {
    if (!opp.submission_deadline || opp.status !== 'open') { return false; }
    return moment(opp.submission_deadline).isBefore(moment(), 'day');
  }

  /** Last activity formatted as i18n relative time (no moment locale dependency). */
  formatLastActivity(opp: Opportunity): string {
    if (!opp.last_activity_at) { return '--'; }
    const diffHours = moment().diff(moment(opp.last_activity_at), 'hours');
    if (diffHours < 1) {
      return this.translate.instant('crm.time.just_now');
    }
    if (diffHours < 24) {
      return this.translate.instant('crm.time.hours_ago', { n: diffHours });
    }
    const diffDays = moment().diff(moment(opp.last_activity_at), 'days');
    if (diffDays === 0) {
      return this.translate.instant('crm.time.today');
    }
    return this.translate.instant('crm.time.days_ago', { n: diffDays });
  }

  private async loadSummary(): Promise<void> {
    try {
      const s = await this.opportunityService.summary();
      this.summary = s ?? null;
      this.statusCounts = s?.opportunities?.by_status ?? {};
      this.updateTabLabels();
    } catch {
      // Non-fatal — tabs still work without counts.
    }
  }

  private updateTabLabels(): void {
    this.tabs.forEach((tab, i) => {
      const chip = this.statusChips[i];
      const count = this.chipCount(chip.key);
      const baseTitle = this.translate.instant(chip.translateKey);
      tab.title = count !== null ? `${baseTitle} (${count})` : baseTitle;
    });
  }

  async load(): Promise<void> {
    // Reload after a delete: stay on the page the user is reading rather than
    // jumping back to the first one.
    this.events.next(DualAxisEvent.REFRESH);
  }

  /** @param reloadOnClose reload the list and tab counts when the popup
   *  closes (a create is not in the list yet), unless it closed for a link
   *  navigation away from this page. */
  openOpportunity(opp: Opportunity, reloadOnClose = false): void {
    const componentContext: any = { id: opp.id, embedded: true };
    const shareUrl = `/opportunities/${opp.id}`;
    const backUrl = this.location.path();
    this.location.go(shareUrl);

    const dialogRef = this.dialogService.open<PopupDialogComponent<OpportunityDetailComponent>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      dialogClass: 'wider-popup',
      context: {
        comp: OpportunityDetailComponent,
        componentContext
      }
    });

    const locSub = this.location.subscribe(() => dialogRef.close());
    this.subs.push(dialogRef.onClose.subscribe(result => {
      locSub.unsubscribe();
      if (this.location.path() === shareUrl) {
        this.location.go(backUrl);
      }
      if (!result?.navigated && (result?.deleted || reloadOnClose)) {
        void this.load();
        void this.loadSummary();
      }
    }));
  }

  canDeleteRow(opp: Opportunity): boolean {
    return (opp.submissions_count ?? 0) === 0 && !opp.converted_Project_id;
  }

  async deleteRow(opp: Opportunity, event: Event): Promise<void> {
    event.stopPropagation();
    if (!confirm(this.translate.instant('crm.delete_confirm'))) { return; }
    await this.opportunityService.deleteOpportunity(opp.id);
    await this.load();
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'CRM Opportunities';
  }
}
